import { Mutation } from "@apollo/client/react/components";
import React from "react";
import { MobileScreen, MobileScreenHeader, MobileScreenBody } from "~/components/mobile";
import { Margin } from "~/components/spacings";
import * as palette from "@govlaunch/palette";
import SuggestProduct from "~/lib/mutations/SuggestProduct";
import {
  ISuggestProductMutationVariables,
  ISuggestProductMutation,
} from "~/lib/mutations/__generated__/SuggestProduct.generated";
import validate from "~/lib/utils/validate";
import { Field, Form as FinalForm } from "react-final-form";
import { FORM_ERROR } from "final-form";
import FieldValidationError from "~/components/form/FieldValidationError";
import { Button } from "@govlaunch/core";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";

interface ISuggestProductPopoveMobilerProps {
  onRequestClose: () => any;
  onSuggestProduct: () => any;
}

export default function SuggestProductPopoverMobile({
  onRequestClose,
  onSuggestProduct,
}: ISuggestProductPopoveMobilerProps) {
  const intl = useIntl();

  const productSuggestionValidation = Yup.object().shape({
    name: Yup.string().required(
      intl.formatMessage({
        defaultMessage: `Can't be blank`,
        id: "Bom3o3",
      }),
    ),
    url: Yup.string().url(
      intl.formatMessage({
        defaultMessage: "Invalid URL",
        id: "SASA4f",
      }),
    ),
  });

  const maxProductSuggestions = intl.formatMessage({
    defaultMessage: `You've reached the limit of 50 product suggestions per day`,
    id: "Hbm59/",
    description: "Error message for when a user reaches the limit of product suggestions",
  });
  const couldntAddProductSuggestion = intl.formatMessage({
    defaultMessage: "Could not add product suggestion",
    id: "+FLj1P",
  });

  return (
    <MobileScreen isOpen={true}>
      <>
        <MobileScreenHeader onRequestClose={onRequestClose}>
          <FormattedMessage defaultMessage="Product suggestion" id="DrBFWv" />
        </MobileScreenHeader>

        <MobileScreenBody>
          <Margin mb={20}>
            <div>
              <Margin mb={3}>
                <p
                  css={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#086BE5",
                  }}
                >
                  <FormattedMessage defaultMessage="See something missing?" id="RUHglt" />
                </p>
              </Margin>
              <p
                css={{
                  color: palette.mediumGray,
                  fontSize: 12,
                }}
              >
                <FormattedMessage
                  defaultMessage="Help us improve our marketplace, submit your suggestion!"
                  id="4FU9ZP"
                />
              </p>
            </div>
          </Margin>

          <Mutation<ISuggestProductMutation, ISuggestProductMutationVariables> mutation={SuggestProduct}>
            {(suggestProduct) => (
              <FinalForm
                onSubmit={async (data) => {
                  try {
                    await suggestProduct({
                      variables: data,
                    } as any);

                    onSuggestProduct();
                  } catch (e: any) {
                    if (/maximum number of product suggestions/i.test(e.message)) {
                      return {
                        [FORM_ERROR]: maxProductSuggestions,
                      };
                    }

                    return {
                      [FORM_ERROR]: couldntAddProductSuggestion,
                    };
                  }
                }}
                validate={validate(productSuggestionValidation)}
              >
                {({ handleSubmit, invalid, submitError }) => (
                  <form
                    onSubmit={handleSubmit}
                    autoComplete="off"
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      novalidate: true,
                    }}
                  >
                    <Field name="name">
                      {({ input, meta }) => (
                        <Margin mb={20}>
                          <input
                            {...input}
                            placeholder={intl.formatMessage({
                              defaultMessage: "Product name",
                              id: "rs6XNw",
                            })}
                            type="text"
                            css={{
                              ":focus": {
                                borderColor: palette.darkSkyBlue,
                              },

                              height: 50,
                              border: `solid 1px ${palette.lightestGray}`,
                              borderRadius: 4,
                              padding: "0 15px",
                              outline: "none",
                              width: "100%",
                            }}
                          />
                          <FieldValidationError meta={meta} />
                        </Margin>
                      )}
                    </Field>

                    <Field name="url">
                      {({ input, meta }) => (
                        <Margin mb={20}>
                          <input
                            {...input}
                            placeholder={intl.formatMessage({
                              defaultMessage: "Paste a link to this product (optional)",
                              id: "qeOI5p",
                            })}
                            css={{
                              ":focus": {
                                borderColor: palette.darkSkyBlue,
                              },
                              type: "url",
                              height: 50,
                              border: `solid 1px ${palette.lightestGray}`,
                              borderRadius: 4,
                              marginBottom: 20,
                              padding: "0 15px",
                              outline: "none",
                              width: "100%",
                            }}
                          />
                          <FieldValidationError meta={meta} />
                        </Margin>
                      )}
                    </Field>

                    <div
                      css={{
                        display: "flex",
                      }}
                    >
                      <Button
                        type="submit"
                        theme="primary"
                        color="#086BE5"
                        textColor="#ffffff"
                        disabled={invalid}
                        css={{
                          width: "100%",
                          marginRight: 10,
                          ":disabled": {
                            opacity: 0.8,
                          },
                        }}
                      >
                        <FormattedMessage defaultMessage="Suggest Product" id="ofg0+9" />
                      </Button>

                      <Button
                        theme="secondary"
                        color="#3F4A5A"
                        css={{
                          marginLeft: 10,
                          width: "100%",
                          borderColor: "#ADB6C3",
                        }}
                        onClick={onRequestClose}
                      >
                        <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
                      </Button>
                    </div>

                    {submitError && (
                      <Margin mt={8}>
                        <div css={{ color: palette.red, fontSize: 14 }}>{submitError}</div>
                      </Margin>
                    )}
                  </form>
                )}
              </FinalForm>
            )}
          </Mutation>
        </MobileScreenBody>
      </>
    </MobileScreen>
  );
}
